import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Plus, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

// Document types that have predefined checklists
const DOC_TYPES_WITH_CHECKLIST = [
  { value: "passport", label: "Passeport" },
  { value: "identity", label: "Carte d'identité" },
  { value: "gray_card", label: "Carte grise" },
  { value: "driver_license", label: "Permis de conduire" },
  { value: "health", label: "Carte Vitale" },
  { value: "residence_permit", label: "Titre de séjour" },
  { value: "vehicle_insurance", label: "Assurance auto" },
  { value: "technical_control", label: "Contrôle technique" },
  { value: "kbis", label: "Kbis" },
  { value: "professional_card", label: "Carte professionnelle" },
  { value: "work_permit", label: "Autorisation de travail" },
] as const;

const PRIORITIES = [
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "Important" },
  { value: "medium", label: "Normal" },
  { value: "low", label: "Optionnel" },
] as const;

const formSchema = z.object({
  title: z.string().min(1, "Le titre est requis"),
  targetDocType: z.string().optional(),
  dueDate: z.date().optional(),
  priority: z.enum(["urgent", "high", "medium", "low"]),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CreateManualActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreateManualActionDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateManualActionDialogProps) {
  const queryClient = useQueryClient();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      targetDocType: undefined,
      dueDate: undefined,
      priority: "medium",
      notes: "",
    },
  });

  const createAction = useMutation({
    mutationFn: async (data: FormData) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Non authentifié");

      // Use type assertion for new columns not yet in generated types
      const { error } = await supabase.from("document_actions").insert({
        user_id: user.user.id,
        document_id: null, // Manual action without document
        action_type: "renew",
        label: data.title,
        status: "pending",
        priority: data.priority,
        due_date: data.dueDate?.toISOString().split('T')[0] || null, // Convert to date string
        metadata: {
          custom_title: data.title,
          target_doc_type: data.targetDocType || null,
          notes: data.notes || null,
        },
      } as any);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Action créée !");
      form.reset();
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["actions"] });
      onSuccess?.();
    },
    onError: () => {
      toast.error("Erreur lors de la création");
    },
  });

  const onSubmit = (data: FormData) => {
    createAction.mutate(data);
  };

  const handleSubmit = form.handleSubmit(onSubmit);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nouvelle action</DialogTitle>
          <DialogDescription>
            Créez une action personnalisée pour planifier une démarche
            administrative
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Titre *</Label>
            <Input
              id="title"
              placeholder="Ex: Passeport de Lucas"
              {...form.register("title")}
            />
            {form.formState.errors.title && (
              <p className="text-xs text-destructive">
                {form.formState.errors.title.message}
              </p>
            )}
          </div>

          {/* Document type (optional) */}
          <div className="space-y-2">
            <Label htmlFor="docType">Type de document (optionnel)</Label>
            <Select
              value={form.watch("targetDocType") || ""}
              onValueChange={(v) =>
                form.setValue("targetDocType", v || undefined)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un type" />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPES_WITH_CHECKLIST.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Sélectionnez un type pour afficher la checklist suggérée
            </p>
          </div>

          {/* Due date */}
          <div className="space-y-2">
            <Label>Date limite (optionnel)</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !form.watch("dueDate") && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.watch("dueDate")
                    ? format(form.watch("dueDate")!, "PPP", { locale: fr })
                    : "Choisir une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={form.watch("dueDate")}
                  onSelect={(date) => {
                    form.setValue("dueDate", date);
                    setCalendarOpen(false);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priorité</Label>
            <Select
              value={form.watch("priority")}
              onValueChange={(v) =>
                form.setValue("priority", v as FormData["priority"])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optionnel)</Label>
            <Textarea
              id="notes"
              placeholder="Informations complémentaires..."
              rows={3}
              {...form.register("notes")}
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={createAction.isPending}>
              {createAction.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Créer l'action
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
